import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { supabase } from '../../config/supabase';
import { SupabaseCourseRow } from '../../types/supabase';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Trash2, PlusCircle, Edit2, Image as ImageIcon, X, GraduationCap, Clock, Users, Award, Calendar, CreditCard, Plus, Minus, ToggleLeft } from 'lucide-react';
import { prepareImageForUpload, uploadCourseImage } from '../../utils/storage';

interface CourseForm {
  courseTitle: string;
  courseSubtitle: string;
  courseDescription: string;
  courseDuration: string;
  courseFormat: 'Presencial' | 'Online' | 'Presencial + Online' | 'Híbrido';
  courseLevel: 'Iniciante' | 'Intermediário' | 'Avançado' | 'Intermediário/Avançado';
  coursePrice: string;
  courseHighlights: string;
  courseCategory: string;
  courseMaxStudents: number;
  coursePrerequisites: string;
  courseStartDate: string;
  courseEndDate: string;
  courseSchedule: string;
  courseLocation: string;
  coursePaymentMethods: string[];
  courseInstallments: {
    quantity: number;
    value: string;
  }[];
}

interface Course {
  courseId: string;
  courseTitle: string;
  courseSubtitle: string;
  courseDescription: string;
  courseDuration: string;
  courseFormat: 'Presencial' | 'Online' | 'Presencial + Online' | 'Híbrido';
  courseLevel: 'Iniciante' | 'Intermediário' | 'Avançado' | 'Intermediário/Avançado';
  coursePrice: string;
  courseImage: string;
  courseHighlights: string[];
  courseCategory: string;
  courseMaxStudents: number;
  coursePrerequisites: string;
  courseStartDate?: string | null;
  courseEndDate?: string | null;
  courseSchedule?: string;
  courseLocation?: string;
  coursePaymentMethods?: string[];
  courseInstallments?: {
    quantity: number;
    value: string;
  }[];
  courseCreatedAt: string;
  courseUpdatedAt: string;
  courseStatus: string;
  courseEnrolledStudents: number;
  userId?: string;
  clientId?: string;
}

const CoursesEditor = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isPreparingImage, setIsPreparingImage] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [coursesList, setCoursesList] = useState<Course[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [installments, setInstallments] = useState<{quantity: number, value: string}[]>([
    { quantity: 1, value: '' }
  ]);
  const { user, selectedClient } = useAuth();
  const currentClientId = selectedClient?.clientId;
  const navigate = useNavigate();
  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<CourseForm>();

  useEffect(() => {
    return () => {
      if (imagePreview?.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const paymentMethodOptions = [
    'PIX',
    'Cartão de Crédito',
    'Cartão de Débito',
    'Boleto Bancário',
    'Transferência Bancária'
  ];

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user || !user.userId) {
        navigate('/login');
        return;
      }

      if (!currentClientId) {
        toast.error('Selecione uma clínica para gerenciar cursos.');
        navigate('/selecionar-cliente');
        return;
      }

      try {
        const { data, error } = await supabase
          .from('user_clients')
          .select('userRole')
          .eq('userId', user.userId)
          .eq('clientId', currentClientId)
          .single();

        if (error || !data) {
          toast.error('Erro: Permissões de usuário não encontradas ou acesso não autorizado');
          navigate('/');
          return;
        }

        if (data.userRole !== 'admin') {
          toast.error('Acesso não autorizado');
          navigate('/');
          return;
        }

        setIsAdmin(true);
        fetchCourses(currentClientId);
      } catch (error) {
        console.error('Erro ao verificar permissões:', error);
        toast.error('Erro ao verificar permissões');
        navigate('/');
      }
    };

    checkAdminStatus();
  }, [user, currentClientId, navigate]);

  const fetchCourses = async (clientId?: string) => {
    const scopedClientId = clientId ?? currentClientId;
    if (!scopedClientId) {
      setCoursesList([]);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('courses')
        .select('*')
        .eq('clientId', scopedClientId)
        .order('courseCreatedAt', { ascending: false });

      if (error) {
        throw error;
      }

      setCoursesList(data as Course[]);
    } catch (error) {
      console.error('Erro ao carregar cursos:', error);
      toast.error('Erro ao carregar cursos');
    }
  };

  const handleEdit = (course: Course) => {
    setEditingCourse(course);
    setShowEditor(true);
    setSelectedFile(null);
    setValue("courseTitle", course.courseTitle);
    setValue("courseSubtitle", course.courseSubtitle);
    setValue("courseDescription", course.courseDescription);
    setValue("courseDuration", course.courseDuration);
    setValue("courseFormat", course.courseFormat as CourseForm["courseFormat"]);
    setValue("courseLevel", course.courseLevel as CourseForm["courseLevel"]);
    setValue("coursePrice", course.coursePrice);
    setValue("courseHighlights", course.courseHighlights?.join("\n") || "");
    setValue("courseCategory", course.courseCategory);
    setValue("courseMaxStudents", course.courseMaxStudents || 0);
    setValue("coursePrerequisites", course.coursePrerequisites || "");
    setValue("courseStartDate", course.courseStartDate ? new Date(course.courseStartDate).toISOString().split("T")[0] : "");
    setValue("courseEndDate", course.courseEndDate ? new Date(course.courseEndDate).toISOString().split("T")[0] : "");
    setValue("courseSchedule", course.courseSchedule || "");
    setValue("courseLocation", course.courseLocation || "");
    setValue("coursePaymentMethods", course.coursePaymentMethods || []);
    setInstallments(course.courseInstallments || [{ quantity: 1, value: "" }]);
    setImagePreview(course.courseImage || null);
  };

  const handleImageChange = async (file: File) => {
    setIsPreparingImage(true);

    try {
      const preparedFile = await prepareImageForUpload(file);
      const previewUrl = URL.createObjectURL(preparedFile);

      if (preparedFile.size < file.size) {
        toast.info('Imagem otimizada automaticamente para o upload.');
      }

      setSelectedFile(preparedFile);
      setImagePreview(previewUrl);
    } catch (error) {
      console.error('Error handling image:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erro ao processar imagem';
      toast.error(errorMessage);
    } finally {
      setIsPreparingImage(false);
    }
  };

  const addInstallment = () => {
    setInstallments([...installments, { quantity: installments.length + 1, value: '' }]);
  };

  const removeInstallment = (index: number) => {
    if (installments.length > 1) {
      setInstallments(installments.filter((_, i) => i !== index));
    }
  };

  const updateInstallment = (index: number, field: 'quantity' | 'value', value: string | number) => {
    const updated = [...installments];
    updated[index] = { ...updated[index], [field]: value };
    setInstallments(updated);
  };

  const onSubmit = async (data: CourseForm) => {
    if (!user || !isAdmin || !currentClientId) {
      toast.error("Você não tem permissão para gerenciar cursos ou cliente não selecionado");
      return;
    }

    if (!selectedFile && !imagePreview) {
      toast.error("Adicione uma imagem para o curso");
      return;
    }

    setIsLoading(true);
    try {
      let imageUrl = imagePreview;
      
      // Only upload new image if a file is selected
      if (selectedFile) {
        imageUrl = await uploadCourseImage(selectedFile);
      }

      if (!imageUrl) {
        throw new Error("Erro ao processar imagem");
      }

      const now = new Date().toISOString();
      const courseData: Omit<SupabaseCourseRow, "courseId"> = {
        clientId: currentClientId,
        userId: user.userId,
        courseTitle: data.courseTitle.trim(),
        courseSubtitle: data.courseSubtitle.trim(),
        courseDescription: data.courseDescription.trim(),
        courseDuration: data.courseDuration.trim(),
        courseFormat: data.courseFormat as Course["courseFormat"],
        courseLevel: data.courseLevel as Course["courseLevel"],
        coursePrice: data.coursePrice.trim(),
        courseImage: imageUrl,
        courseHighlights: data.courseHighlights.split("\n").map(h => h.trim()).filter(h => h.length > 0),
        courseCategory: data.courseCategory,
        courseMaxStudents: data.courseMaxStudents,
        coursePrerequisites: data.coursePrerequisites.trim(),
        courseStartDate: data.courseStartDate ? new Date(data.courseStartDate).toISOString() : undefined,
        courseEndDate: data.courseEndDate ? new Date(data.courseEndDate).toISOString() : undefined,
        courseSchedule: data.courseSchedule?.trim() || '',
        courseLocation: data.courseLocation?.trim() || '',
        coursePaymentMethods: data.coursePaymentMethods || [],
        courseInstallments: installments.filter(inst => inst.value.trim() !== ''),
        courseCreatedAt: editingCourse ? editingCourse.courseCreatedAt : now,
        courseUpdatedAt: now,
        courseStatus: 'active',
        courseEnrolledStudents: editingCourse ? editingCourse.courseEnrolledStudents : 0
      };

      if (editingCourse) {
        const { error } = await supabase
          .from('courses')
          .update(courseData)
          .eq('courseId', editingCourse.courseId)
          .eq('clientId', currentClientId);
        
        if (error) throw error;
        toast.success("Curso atualizado com sucesso!");
      } else {
        const { error } = await supabase
          .from('courses')
          .insert(courseData);
        
        if (error) throw error;
        toast.success("Curso criado com sucesso!");
      }

      reset();
      setShowEditor(false);
      setEditingCourse(null);
      setImagePreview(null);
      setSelectedFile(null);
      setInstallments([{ quantity: 1, value: "" }]);
      fetchCourses(currentClientId);
    } catch (error) {
      console.error("Error saving course:", error);
      const errorMessage = error instanceof Error ? error.message : "Erro ao salvar curso";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (courseId: string) => {
    if (!currentClientId) {
      toast.error('Selecione uma clínica para excluir cursos.');
      return;
    }

    try {
      const { error } = await supabase
        .from('courses')
        .delete()
        .eq('courseId', courseId)
        .eq('clientId', currentClientId);
      
      if (error) throw error;
      toast.success('Curso excluído com sucesso!');
      fetchCourses(currentClientId);
    } catch (error) {
      console.error('Erro ao excluir curso:', error);
      toast.error('Erro ao excluir curso');
    } finally {
      setShowDeleteConfirm(null);
    }
  };

  const toggleCourseStatus = async (course: Course) => {
    if (!currentClientId) {
      toast.error('Selecione uma clínica para alterar status de cursos.');
      return;
    }

    try {
      const newStatus = course.courseStatus === 'active' ? 'inactive' : 'active';
      const { error } = await supabase
        .from('courses')
        .update({ courseStatus: newStatus })
        .eq('courseId', course.courseId)
        .eq('clientId', currentClientId);

      if (error) throw error;
      toast.success(`Curso ${newStatus === 'active' ? 'ativado' : 'inativado'} com sucesso!`);
      fetchCourses(currentClientId);
    } catch (error) {
      console.error('Erro ao atualizar status do curso:', error);
      toast.error('Erro ao atualizar status do curso');
    }
  };

  

  const formatPrice = (price: string) => {
    return price.startsWith('R$') ? price : `R$ ${price}`;
  };

  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) return 'Não definida';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold">Gerenciar Cursos</h2>
        {!showEditor && (
          <button
            onClick={() => {
              setEditingCourse(null);
              reset();
              setImagePreview(null);
              setSelectedFile(null);
              setInstallments([{ quantity: 1, value: '' }]);
              setShowEditor(true);
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
          >
            <PlusCircle className="w-4 h-4 mr-2" />
            Novo Curso
          </button>
        )}
      </div>

      {showEditor ? (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-xl font-semibold mb-6">
            {editingCourse ? 'Editar Curso' : 'Novo Curso'}
          </h3>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Título <span className="ml-2 text-sm font-light text-gray-400">(obrigatório)</span></label>
                <input
                  type="text"
                  {...register('courseTitle', { required: 'Título é obrigatório' })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
                {errors.courseTitle && (
                  <p className="mt-1 text-sm text-red-600">{errors.courseTitle.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Subtítulo <span className="ml-2 text-sm font-light text-gray-400">(obrigatório)</span></label>
                <input
                  type="text"
                  {...register('courseSubtitle', { required: 'Subtítulo é obrigatório' })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
                {errors.courseSubtitle && (
                  <p className="mt-1 text-sm text-red-600">{errors.courseSubtitle.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Duração <span className="ml-2 text-sm font-light text-gray-400">(obrigatório)</span></label>
                <input
                  type="text"
                  placeholder="Ex: 40 horas"
                  {...register('courseDuration', { required: 'Duração é obrigatória' })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
                <div className="mt-2 flex items-center gap-2">
                  <select
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v) setValue('courseDuration', v);
                    }}
                    className="rounded-md border-gray-200 text-sm"
                    defaultValue=""
                  >
                    <option value="">Predefinições</option>
                    <option value="30 minutos">30 minutos</option>
                    <option value="45 minutos">45 minutos</option>
                    <option value="1 hora">1 hora</option>
                    <option value="2 horas">2 horas</option>
                    <option value="4 horas">4 horas</option>
                    <option value="8 horas">8 horas</option>
                    <option value="40 horas">40 horas</option>
                  </select>
                  <p className="text-sm text-gray-500">Escolha para preencher automaticamente</p>
                </div>
                {errors.courseDuration && (
                  <p className="mt-1 text-sm text-red-600">{errors.courseDuration.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Formato <span className="ml-2 text-sm font-light text-gray-400">(obrigatório)</span></label>
                <select
                  {...register('courseFormat', { required: 'Formato é obrigatório' })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                >
                  <option value="">Selecione o formato</option>
                  <option value="Presencial">Presencial</option>
                  <option value="Online">Online</option>
                  <option value="Presencial + Online">Presencial + Online</option>
                  <option value="Híbrido">Híbrido</option>
                </select>
                {errors.courseFormat && (
                  <p className="mt-1 text-sm text-red-600">{errors.courseFormat.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Nível <span className="ml-2 text-sm font-light text-gray-400">(obrigatório)</span></label>
                <select
                  {...register('courseLevel', { required: 'Nível é obrigatório' })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                >
                  <option value="">Selecione o nível</option>
                  <option value="Iniciante">Iniciante</option>
                  <option value="Intermediário">Intermediário</option>
                  <option value="Avançado">Avançado</option>
                  <option value="Intermediário/Avançado">Intermediário/Avançado</option>
                </select>
                {errors.courseLevel && (
                  <p className="mt-1 text-sm text-red-600">{errors.courseLevel.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Preço <span className="ml-2 text-sm font-light text-gray-400">(obrigatório)</span></label>
                <input
                  type="text"
                  placeholder="Ex: R$ 1.200 ou 1.200"
                  {...register('coursePrice', { required: 'Preço é obrigatório' })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
                {errors.coursePrice && (
                  <p className="mt-1 text-sm text-red-600">{errors.coursePrice.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Data de Início</label>
                <input
                  type="date"
                  {...register('courseStartDate')}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Data de Término</label>
                <input
                  type="date"
                  {...register('courseEndDate')}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Horário</label>
                <input
                  type="text"
                  placeholder="Ex: 08:00 às 17:00"
                  {...register('courseSchedule')}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
                <div className="mt-2 flex items-center gap-2">
                  <select
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v) setValue('courseSchedule', v);
                    }}
                    className="rounded-md border-gray-200 text-sm"
                    defaultValue=""
                  >
                    <option value="">Predefinições</option>
                    <option value="08:00 às 17:00">08:00 às 17:00</option>
                    <option value="09:00 às 18:00">09:00 às 18:00</option>
                    <option value="19:00 às 22:00">19:00 às 22:00</option>
                    <option value="08:00 às 12:00">08:00 às 12:00</option>
                  </select>
                  <p className="text-sm text-gray-500">Escolha para preencher automaticamente</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Local</label>
                <input
                  type="text"
                  placeholder="Ex: Auditório Revalidatie"
                  {...register('courseLocation')}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Categoria <span className="ml-2 text-sm font-light text-gray-400">(obrigatório)</span></label>
                <select
                  {...register('courseCategory', { required: 'Categoria é obrigatória' })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                >
                  <option value="">Selecione uma categoria</option>
                  <option value="Reabilitação Pulmonar">Reabilitação Pulmonar</option>
                  <option value="Fisioterapia Respiratória">Fisioterapia Respiratória</option>
                  <option value="Reabilitação Cardíaca">Reabilitação Cardíaca</option>
                  <option value="UTI Respiratória">UTI Respiratória</option>
                  <option value="Avaliação Funcional">Avaliação Funcional</option>
                  <option value="Especialização">Especialização</option>
                </select>
                {errors.courseCategory && (
                  <p className="mt-1 text-sm text-red-600">{errors.courseCategory.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Máximo de Alunos <span className="ml-2 text-sm font-light text-gray-400">(obrigatório)</span></label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  {...register('courseMaxStudents', {
                    required: 'Número máximo de alunos é obrigatório',
                    min: { value: 1, message: 'Mínimo de 1 aluno' },
                    max: { value: 100, message: 'Máximo de 100 alunos' }
                  })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
                {errors.courseMaxStudents && (
                  <p className="mt-1 text-sm text-red-600">{errors.courseMaxStudents.message}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Descrição <span className="ml-2 text-sm font-light text-gray-400">(obrigatório)</span></label>
              <textarea
                {...register('courseDescription', { required: 'Descrição é obrigatória' })}
                rows={4}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
              {errors.courseDescription && (
                <p className="mt-1 text-sm text-red-600">{errors.courseDescription.message}</p>
                )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Destaques do Curso <span className="ml-2 text-sm font-light text-gray-400">(obrigatório)</span></label>
              <textarea
                {...register('courseHighlights', { required: 'Destaques são obrigatórios' })}
                rows={6}
                placeholder="Digite um destaque por linha, exemplo:&#10;Avaliação funcional respiratória&#10;Prescrição de exercícios específicos&#10;Manejo de pacientes com DPOC"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
              <p className="mt-1 text-sm text-gray-500">Digite um destaque por linha</p>
              {errors.courseHighlights && (
                <p className="mt-1 text-sm text-red-600">{errors.courseHighlights.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Pré-requisitos</label>
              <textarea
                {...register('coursePrerequisites')}
                rows={3}
                placeholder="Descreva os pré-requisitos necessários para o curso"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
            </div>

            {/* Formas de Pagamento */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Formas de Pagamento</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {paymentMethodOptions.map((method) => (
                  <label key={method} className="flex items-center">
                    <input
                      type="checkbox"
                      value={method}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      {...register('coursePaymentMethods')}
                    />
                    <span className="ml-2 text-sm text-gray-700">{method}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Parcelamento */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">Opções de Parcelamento</label>
                <button
                  type="button"
                  onClick={addInstallment}
                  className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-primary-600 bg-primary-100 hover:bg-primary-200"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar
                </button>
              </div>
              <div className="space-y-3">
                {installments.map((installment, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="flex-1">
                      <input
                        type="number"
                        min="1"
                        placeholder="Parcelas"
                        value={installment.quantity}
                        onChange={(e) => updateInstallment(index, 'quantity', parseInt(e.target.value) || 1)}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      />
                    </div>
                    <span className="text-sm text-gray-500">x</span>
                    <div className="flex-2">
                      <input
                        type="text"
                        placeholder="R$ 100,00"
                        value={installment.value}
                        onChange={(e) => updateInstallment(index, 'value', e.target.value)}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      />
                    </div>
                    {installments.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeInstallment(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Imagem do Curso</label>
              <div className="space-y-4">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  disabled={isPreparingImage || isLoading}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      handleImageChange(file);
                    }
                  }}
                  className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-md file:border-0
                    file:text-sm file:font-medium
                    file:bg-primary-50 file:text-primary-700
                    hover:file:bg-primary-100"
                />
                {isPreparingImage && (
                  <p className="text-sm text-gray-500">Processando imagem...</p>
                )}
                {imagePreview && (
                  <div className="relative w-full max-w-2xl mx-auto">
                    <div className="aspect-w-16 aspect-h-9 rounded-lg overflow-hidden bg-gray-100">
                      <img 
                        src={imagePreview} 
                        alt="Preview" 
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setImagePreview(null);
                          setSelectedFile(null);
                        }}
                        className="absolute top-2 right-2 p-1 bg-white rounded-full text-gray-500 hover:text-gray-700"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowEditor(false);
                  setEditingCourse(null);
                  setImagePreview(null);
                  setSelectedFile(null);
                  setInstallments([{ quantity: 1, value: '' }]);
                  reset();
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isLoading || isPreparingImage}
                className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {isLoading ? 'Salvando...' : isPreparingImage ? 'Processando imagem...' : editingCourse ? 'Salvar Alterações' : 'Criar Curso'}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Curso
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Detalhes
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Datas
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Alunos
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {coursesList.map((course) => (
                  <tr key={course.courseId} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {course.courseImage && (
                          <img
                            src={course.courseImage}
                            alt={course.courseTitle}
                            className="h-12 w-12 rounded-md object-cover mr-4"
                          />
                        )}
                        <div>
                          <div className="flex items-center">
                            <div className="text-sm font-medium text-gray-900">{course.courseTitle}</div>
                            {course.courseStatus === 'inactive' && (
                              <span className="ml-2 text-xs text-gray-500">(Inativo)</span>
                            )}
                            {course.courseStatus === 'archived' && (
                              <span className="ml-2 text-xs text-amber-600">(Arquivado)</span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">{course.courseSubtitle}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <div className="flex items-center mb-1">
                          <Clock className="w-4 h-4 text-gray-400 mr-1" />
                          {course.courseDuration}
                        </div>
                        <div className="flex items-center mb-1">
                          <GraduationCap className="w-4 h-4 text-gray-400 mr-1" />
                          {course.courseLevel}
                        </div>
                        <div className="text-primary-600 font-medium">{formatPrice(course.coursePrice)}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <div className="flex items-center mb-1">
                          <Calendar className="w-4 h-4 text-gray-400 mr-1" />
                          {formatDate(course.courseStartDate)}
                        </div>
                        {course.courseEndDate && (
                          <div className="text-sm text-gray-500">
                            até {formatDate(course.courseEndDate)}
                          </div>
                        )}
                        {course.courseSchedule && (
                          <div className="text-sm text-gray-500">
                            {course.courseSchedule}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <div className="flex items-center">
                          <Users className="w-4 h-4 text-gray-400 mr-1" />
                          {course.courseEnrolledStudents || 0}/{course.courseMaxStudents}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2 items-center">
                        <button
                          onClick={() => toggleCourseStatus(course)}
                          className={`p-1 rounded ${course.courseStatus === 'active' ? 'text-emerald-600' : 'text-gray-400'} hover:opacity-80`}
                          title={course.courseStatus === 'active' ? 'Inativar curso' : 'Ativar curso'}
                        >
                          <ToggleLeft className="w-5 h-5" />
                        </button>
                        {/* Arquivar removed per request - keep only toggle, edit and delete */}
                        <button
                          onClick={() => handleEdit(course)}
                          className="text-primary-600 hover:text-primary-900 p-1"
                          title="Editar"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(course.courseId)}
                          className="text-red-600 hover:text-red-900 p-1"
                          title="Excluir"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {coursesList.length === 0 && (
              <div className="text-center py-8">
                <GraduationCap className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Nenhum curso criado ainda</p>
              </div>
            )}
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Confirmar Exclusão</h3>
            <p className="text-gray-600 mb-6">
              Tem certeza que deseja excluir este curso? Esta ação não pode ser desfeita.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm)}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoursesEditor;
